import { useNavigate } from 'react-router-dom';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { EmptyState } from '@components/empty-state';
import { useDeleteModal } from '@components/generic-modals';
import { Header } from '@components/header';
import { IconAI } from '@components/icons/ai';
import { IconCopy } from '@components/icons/copy';
import { IconEdit } from '@components/icons/edit';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconReadMore } from '@components/icons/read-more';
import { IconTrash } from '@components/icons/trash';
import { ListCard } from '@components/list-card';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { PageContent } from '@components/page-containers/page-container';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { showSuccess } from '@components/toaster';
import { ValueList } from '@components/value-list';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { useMCPServers, useDeleteMCPServer, useMCPServerJourneyCounts } from 'api/mcp/hooks';

import { useCreateMCPServerModal } from './create-modal';
import { useMCPServerSidebar } from './sidebar';

const MCP_SERVERS_HELP_URL =
  'https://help.journeys.50skills.app/en/articles/356228-mcp-servers-in-journeys';

export const MCPServers = () => {
  const { t } = useScopedTranslate(locale);
  const { data: mcpServers, isLoading } = useMCPServers();
  const serverIds = mcpServers?.map((server) => server.clientId) ?? [];
  const journeyQueries = useMCPServerJourneyCounts(serverIds);
  const showMCPServerSidebar = useMCPServerSidebar();
  const showCreateMCPServerModal = useCreateMCPServerModal();
  const deleteMCPServerMutation = useDeleteMCPServer();
  const showDeleteModal = useDeleteModal();
  const navigate = useNavigate();

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url);
    showSuccess(t('serverUrlCopied'));
  };

  if (isLoading) {
    return <Spinner />;
  }

  const isDataEmpty = !mcpServers || mcpServers.length === 0;

  return (
    <>
      <Header
        actions={
          <Stack orientation="horizontal" space={4}>
            <Button
              data-cy="learn-more-mcp-btn"
              text={t('learnMore')}
              iconRight={<IconOpenNewTab size={16} />}
              iconLeft={<IconReadMore />}
              variant="secondary"
              onClick={() => window.open(MCP_SERVERS_HELP_URL, '_blank')}
            />
            <Button
              data-cy="create-mcp-server-btn"
              text={t('createMCPServer')}
              iconLeft={<IconAI />}
              onClick={() => showCreateMCPServerModal()}
            />
          </Stack>
        }
        breadcrumbs={<Breadcrumb items={[{ label: t('mcpServers') }]} />}
      />
      <PageContent>
        {isDataEmpty ? (
          <EmptyState
            skipMargin
            header={t('emptyStateHeader')}
            content={t('emptyStateContent')}
            icon={<IconAI size={80} />}
          />
        ) : (
          <Stack space={2}>
            {mcpServers?.map((server, index) => {
              const journeyQuery = journeyQueries[index];
              const journeyCount = journeyQuery?.data?.length ?? 0;
              return (
                <ListCard
                  key={server.clientId}
                  title={server.label ?? server.clientId}
                  description={server.description ?? ''}
                  icon={<IconAI size={40} />}
                  onClick={() => navigate(`/settings/mcp-servers/${server.clientId}`)}
                  actions={
                    <Stack orientation="horizontal" space={4} align="center">
                      {server.serverUrl && (
                        <Stack orientation="horizontal" space={2} align="center">
                          <Text
                            font="regular14"
                            text={server.serverUrl}
                            style={{
                              maxWidth: pxToRem(200),
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          />
                          <Button
                            variant="ghost"
                            iconLeft={<IconCopy size={16} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopy(server.serverUrl);
                            }}
                            size="sm"
                          />
                        </Stack>
                      )}
                      <Separator orientation="horizontal" />
                      <ValueList items={[{ value: journeyCount, label: t('journeys') }]} />
                      <Separator orientation="horizontal" />
                      <Menu
                        buttonProps={{
                          variant: 'ghost',
                          iconRight: <IconMoreVertical />,
                        }}
                      >
                        <Menu.Item
                          label={t('core.edit')}
                          icon={<IconEdit />}
                          onSelect={() => showMCPServerSidebar({ existingServer: server })}
                        />
                        <Menu.Item
                          label={t('core.delete')}
                          icon={<IconTrash />}
                          variant="danger"
                          onSelect={() =>
                            showDeleteModal({
                              title: t('deleteTitle'),
                              description: t('deleteDescription'),
                              onConfirm: () => deleteMCPServerMutation.mutate(server.clientId),
                            })
                          }
                        />
                      </Menu>
                    </Stack>
                  }
                />
              );
            })}
          </Stack>
        )}
      </PageContent>
    </>
  );
};

const locale = {
  en: {
    mcpServers: 'MCP Servers',
    createMCPServer: 'Create MCP Server',
    learnMore: 'Learn more',
    emptyStateHeader: 'You do not have any MCP Servers',
    emptyStateContent:
      'Create and manage MCP servers for your company. These servers can be used to integrate with external AI services and enhance your journey workflows',
    deleteTitle: 'Delete MCP Server',
    deleteDescription: 'Are you sure you want to delete this MCP server?',
    serverUrlCopied: 'Server URL copied to clipboard',
    journeys: 'Journeys',
  },
};
